import { Fragment, ReactNode, useMemo, useState } from 'react'
import { clsx } from 'clsx'
import { CheckIcon, SearchIcon } from './icons'

export type SearchableOption = {
  id: string
  label: ReactNode
  searchText: string
  group?: string
  leading?: ReactNode
  trailing?: ReactNode
  selected?: boolean
  disabled?: boolean
}

export type SearchableOptionSearch = (
  items: SearchableOption[],
  query: string
) => SearchableOption[]

export function SearchableOptionMenu({
  title,
  placeholder,
  emptyLabel,
  items,
  searchItems,
  onSelect,
  onRequestClose,
  className
}: {
  title: string
  placeholder: string
  emptyLabel: string
  items: SearchableOption[]
  searchItems?: SearchableOptionSearch
  onSelect: (id: string) => void
  onRequestClose?: () => void
  className?: string
}) {
  const [query, setQuery] = useState('')
  const [activeItemId, setActiveItemId] = useState<string | undefined>()
  const trimmedQuery = query.trim()

  const filteredItems = useMemo(() => {
    if (trimmedQuery == '') {
      return items
    }

    return (searchItems ?? defaultSearchItems)(items, trimmedQuery)
  }, [items, trimmedQuery, searchItems])
  const enabledItems = filteredItems.filter((item) => !item.disabled)

  const manualActiveItemId =
    activeItemId != undefined && enabledItems.some((item) => item.id == activeItemId)
      ? activeItemId
      : undefined
  const highlightedItemId =
    trimmedQuery == '' ? manualActiveItemId : manualActiveItemId ?? enabledItems[0]?.id

  const moveHighlight = (direction: 1 | -1) => {
    if (enabledItems.length == 0) {
      return
    }

    if (highlightedItemId == undefined) {
      const nextItem = direction == 1 ? enabledItems[0] : enabledItems[enabledItems.length - 1]
      setActiveItemId(nextItem?.id)
      return
    }

    const highlightedIndex = enabledItems.findIndex((item) => item.id == highlightedItemId)

    const nextIndex = Math.max(0, Math.min(enabledItems.length - 1, highlightedIndex + direction))
    setActiveItemId(enabledItems[nextIndex]?.id)
  }

  return (
    <div
      className={clsx(
        'flex max-h-72 w-56 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md',
        className
      )}
    >
      <div className="sr-only">{title}</div>
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-gray-200 px-2 focus-within:bg-blue-50">
        <SearchIcon className="size-6 shrink-0 text-gray-600" />
        <input
          autoFocus
          value={query}
          aria-label={placeholder}
          onChange={(event) => {
            setQuery(event.target.value)
            setActiveItemId(undefined)
          }}
          onKeyDown={(event) => {
            switch (event.key) {
              case 'ArrowDown':
                event.preventDefault()
                moveHighlight(1)
                break
              case 'ArrowUp':
                event.preventDefault()
                moveHighlight(-1)
                break
              case 'Enter':
                if (highlightedItemId == undefined) {
                  return
                }

                event.preventDefault()
                onSelect(highlightedItemId)
                break
              case 'Escape':
                if (onRequestClose) {
                  event.preventDefault()
                  event.stopPropagation()
                  onRequestClose()
                }
                break
            }
          }}
          placeholder={placeholder}
          className="block h-full w-full min-w-0 border-0 bg-transparent p-0 text-lg font-normal text-gray-900 placeholder:text-gray-600 focus:outline-none focus:ring-0"
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-auto p-2">
        {filteredItems.length == 0 ? (
          <div className="px-6 py-6 text-sm text-gray-500">{emptyLabel}</div>
        ) : (
          filteredItems.map((item, index) => {
            const isHighlighted = item.id == highlightedItemId
            const isDisabled = item.disabled == true

            return (
              <Fragment key={item.id}>
                {item.group && item.group !== filteredItems[index - 1]?.group && (
                  <div className="px-3 pb-1 pt-3 text-xs font-medium text-gray-500">
                    {item.group}
                  </div>
                )}
                <button
                  type="button"
                  disabled={isDisabled}
                  className={clsx(
                    'flex min-h-9 w-full shrink-0 items-center gap-2 rounded-lg px-3 py-1.5 text-left text-lg',
                    isDisabled
                      ? 'cursor-not-allowed text-gray-400'
                      : 'text-gray-900 hover:bg-blue-100 hover:text-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400',
                    (isHighlighted || item.selected) &&
                      !isDisabled &&
                      'bg-blue-100 text-blue-500 hover:bg-blue-100'
                  )}
                  onMouseMove={() => {
                    if (!isDisabled) {
                      setActiveItemId(item.id)
                    }
                  }}
                  onClick={() => {
                    if (!isDisabled) {
                      onSelect(item.id)
                    }
                  }}
                >
                  {item.leading}
                  <div className="min-w-0 flex-1 truncate font-normal">{item.label}</div>
                  {(item.trailing || item.selected) && (
                    <div className="ml-2 flex shrink-0 items-center gap-2 text-sm">
                      {item.trailing}
                      {item.selected && <CheckIcon className="size-4 text-blue-500" />}
                    </div>
                  )}
                </button>
              </Fragment>
            )
          })
        )}
      </div>
    </div>
  )
}

function defaultSearchItems(items: SearchableOption[], query: string): SearchableOption[] {
  const normalizedQuery = query.toLowerCase()

  return items.filter((item) => matchesQuery(item.searchText, normalizedQuery))
}

function matchesQuery(searchText: string, query: string): boolean {
  const queryTokens = tokenize(query)
  if (queryTokens.length == 0) {
    return true
  }

  const haystackTokens = tokenize(searchText.toLowerCase())
  return queryTokens.every((token) => haystackTokens.some((haystack) => haystack.includes(token)))
}

function tokenize(value: string): string[] {
  return value.split(/[@\s]+/).filter(Boolean)
}
